import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { TopScrollSync } from '@/components/table/TopScrollSync'
import { useDragScroll } from '@/hooks/useDragScroll'
import { DateInput } from '@/components/forms/DateInput'
import { Typeahead } from '@/components/forms/Typeahead'
import { formatDate, formatDateTime } from '@/lib/dateFormat'
import type { Customer, CustomerBuyer } from '@/services/customers'
import type { VariantSearchResult } from '@/services/salesOrders'
import { searchVariantsApi } from '@/services/salesOrders'
import {
  type GridLineItem,
  formatLineDim,
  validateGridLine,
  createEmptyGridLine,
} from './salesOrderGridUtils'
import { SalesOrderRowMenu } from './SalesOrderRowMenu'
import { StatusReasonModal } from './StatusReasonModal'
import { SalesOrderLineHistoryModal } from './SalesOrderLineHistoryModal'
import { PushToDemandModal } from './PushToDemandModal'

export interface SalesOrderGridProps {
  lines: GridLineItem[]
  customers: Customer[]
  customerBuyersMap: Record<string, CustomerBuyer[]>
  variantsPreload?: VariantSearchResult[]
  canWrite: boolean
  canDemandWrite: boolean
  orderId?: string
  orderCode?: string
  onUpdateLines: (newLines: GridLineItem[]) => void
  onLineStatusChange?: (lineId: string, status: 'draft' | 'sx' | 'cancelled', reason?: string) => Promise<void>
  onSave?: () => void
  saving?: boolean
  errorHighlightFields?: { lineId: string; fields: string[] } | null
}

export function SalesOrderGrid({
  lines,
  customers,
  customerBuyersMap,
  variantsPreload = [],
  canWrite,
  canDemandWrite,
  orderId,
  orderCode,
  onUpdateLines,
  onLineStatusChange,
  onSave,
  saving = false,
  errorHighlightFields = null,
}: SalesOrderGridProps): React.ReactElement {
  // Sticky columns toggle (default ON when width >= 1500)
  const [isSticky, setIsSticky] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1500
    }
    return true
  })

  // Table scroll container ref with drag scroll
  const scrollContainerRef = useDragScroll<HTMLDivElement>()

  // Variants cache for Typeahead
  const [variantList, setVariantList] = useState<VariantSearchResult[]>(variantsPreload)
  useEffect(() => {
    if (variantsPreload.length > 0) {
      setVariantList(variantsPreload)
    } else {
      searchVariantsApi('')
        .then((res) => setVariantList(res))
        .catch(() => {})
    }
  }, [variantsPreload])

  // Toast notifications
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => {
      setToastMessage((current) => (current === msg ? null : current))
    }, 3000)
  }, [])

  // Floating 3-dots row menu
  const [menuState, setMenuState] = useState<{
    line: GridLineItem | null
    anchorRect: DOMRect | null
  }>({ line: null, anchorRect: null })

  // Status reason modal state
  const [statusModalState, setStatusModalState] = useState<{
    isOpen: boolean
    line: GridLineItem | null
    targetStatus: 'draft' | 'sx' | 'cancelled'
  }>({ isOpen: false, line: null, targetStatus: 'draft' })

  // Line history modal
  const [historyModalLine, setHistoryModalLine] = useState<GridLineItem | null>(null)

  // Push to demand modal
  const [demandModalLine, setDemandModalLine] = useState<GridLineItem | null>(null)

  // Hover image preview
  const [hoverCard, setHoverCard] = useState<{
    visible: boolean
    line: GridLineItem | null
    x: number
    y: number
  }>({ visible: false, line: null, x: 0, y: 0 })

  // Cell elements refs for keyboard navigation and focus
  const cellRefs = useRef<Record<string, HTMLElement | null>>({})

  // Active customers list for Typeahead
  const activeCustomers = useMemo(() => {
    return customers.filter((c) => c.status === 'active')
  }, [customers])

  // Update line helper
  const updateSingleLine = useCallback(
    (idx: number, updater: (prev: GridLineItem) => GridLineItem) => {
      const copy = [...lines]
      copy[idx] = updater(copy[idx])
      onUpdateLines(copy)
    },
    [lines, onUpdateLines],
  )

  // Customer change
  const handleCustomerChange = (idx: number, cust: Customer | null) => {
    const current = lines[idx]
    const newCustId = cust?.id || ''
    const newCustCode = cust?.code || ''
    const newCustName = cust?.name || ''

    // If customer changed, verify existing buyer
    let newBuyerId = current.buyer_id
    let newBuyerName = current.buyer_name

    if (newCustId !== current.customer_id) {
      const allowedBuyers = customerBuyersMap[newCustId] || []
      const isBuyerCompatible = allowedBuyers.some((b) => b.id === current.buyer_id)
      if (!isBuyerCompatible && current.buyer_id) {
        newBuyerId = null
        newBuyerName = null
        showToast(`Đã xóa người mua do không thuộc khách hàng ${newCustCode}`)
      }
    }

    updateSingleLine(idx, (l) => ({
      ...l,
      customer_id: newCustId,
      customer_code: newCustCode,
      customer_name: newCustName,
      buyer_id: newBuyerId,
      buyer_name: newBuyerName,
      isCopiedCustomer: false, // cleared on user interaction
    }))
  }

  // Buyer change
  const handleBuyerChange = (idx: number, buyer: CustomerBuyer | null) => {
    updateSingleLine(idx, (l) => ({
      ...l,
      buyer_id: buyer?.id ?? null,
      buyer_name: buyer?.name ?? null,
      isCopiedBuyer: false,
    }))
  }

  // Variant change
  const handleVariantChange = (idx: number, v: VariantSearchResult | null) => {
    updateSingleLine(idx, (l) => ({
      ...l,
      variant_id: v?.id || '',
      variant_code: v?.variant_code || '',
      product_id: v?.product_id || '',
      product_name: v?.product_name || '',
      has_image: Boolean(v?.has_image || v?.image_url),
      color_name: v?.color?.name || v?.color_name || '',
      color_swatch_hex: v?.color?.swatch_hex || null,
      w: v?.dimensions?.w ?? v?.w ?? null,
      d: v?.dimensions?.d ?? v?.d ?? null,
      h: v?.dimensions?.h ?? v?.h ?? null,
    }))
  }

  // Add new line
  const handleAddNewLine = useCallback(() => {
    if (!canWrite) return
    const prevLine = lines.length > 0 ? lines[lines.length - 1] : undefined
    const newLine = createEmptyGridLine(prevLine)
    onUpdateLines([...lines, newLine])
    showToast('Đã thêm dòng mới.')
  }, [canWrite, lines, onUpdateLines, showToast])

  // Duplicate line
  const handleDuplicateLine = useCallback(
    (idx: number) => {
      if (!canWrite) return
      const target = lines[idx]
      if (!target) return
      const duplicated: GridLineItem = {
        ...target,
        id: undefined, // fresh line without existing id
        tempKey: `line-dup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        line_status: 'draft',
        status_reason: null,
        demand_status: 'NOT_PUSHED',
        demand_batch_ids: [],
      }
      const updated = [...lines]
      updated.splice(idx + 1, 0, duplicated)
      onUpdateLines(updated)
      showToast(`Đã nhân đôi dòng #${idx + 1}.`)
    },
    [canWrite, lines, onUpdateLines, showToast],
  )

  // Delete line
  const handleDeleteLine = (idx: number) => {
    if (!canWrite) return
    const target = lines[idx]
    if (!target) return
    if (target.line_status !== 'draft') {
      showToast('Chỉ dòng Nháp mới có thể xóa. Hãy Hủy thay vì Xóa!')
      return
    }
    if (target.demand_status === 'PUSHED' || (target.demand_batch_ids && target.demand_batch_ids.length > 0)) {
      showToast('Dòng đã vào nhu cầu, không thể xóa!')
      return
    }
    if (window.confirm('Xác nhận xóa dòng này?')) {
      const updated = [...lines]
      updated.splice(idx, 1)
      onUpdateLines(updated)
      showToast('Đã xóa dòng.')
    }
  }

  // Toggle dimension unit for line
  const handleToggleUnit = (idx: number) => {
    updateSingleLine(idx, (l) => ({
      ...l,
      dim_unit: (l.dim_unit || 'inch') === 'inch' ? 'mm' : 'inch',
    }))
  }

  // Keyboard navigation & shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ctrl + Enter: add line
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault()
        handleAddNewLine()
      }
      // Ctrl + S: save
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        if (onSave && !saving) {
          e.preventDefault()
          onSave()
        }
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [handleAddNewLine, onSave, saving])

  // Confirm status change
  const handleConfirmStatusChange = async (reason?: string) => {
    const { line, targetStatus } = statusModalState
    if (!line) return

    if (onLineStatusChange && line.id) {
      try {
        await onLineStatusChange(line.id, targetStatus, reason)
        showToast(`Đã chuyển trạng thái dòng thành công.`)
      } catch (err) {
        // Handled in parent
      }
    } else {
      // Local state update if not saved yet
      const idx = lines.findIndex((l) => l.tempKey === line.tempKey)
      if (idx !== -1) {
        updateSingleLine(idx, (l) => ({
          ...l,
          line_status: targetStatus,
          status_reason: reason || null,
        }))
        showToast(`Đã đổi trạng thái dòng thành ${targetStatus}.`)
      }
    }
    setStatusModalState({ isOpen: false, line: null, targetStatus: 'draft' })
  }

  return (
    <div className="flex flex-col gap-2 text-sm">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-[9999999] flex items-center gap-3 rounded-md bg-slate-900 px-4 py-3 text-sm text-white shadow-xl animate-in slide-in-from-bottom-2 duration-150">
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Grid Toolbar Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                onClick={handleAddNewLine}
              >
                <span>➕</span> <span>Thêm dòng (Ctrl+Enter)</span>
              </button>
            </>
          )}

          <button
            type="button"
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
              isSticky
                ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
            onClick={() => setIsSticky(!isSticky)}
          >
            <span>📌</span> <span>Ghim cột: {isSticky ? 'BẬT' : 'TẮT'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <span>{lines.length} dòng hàng</span>
          <span>•</span>
          <span>Kéo ngang hoặc dùng thanh cuộn để xem đủ cột</span>
        </div>
      </div>

      {/* Synced Top Scrollbar */}
      <TopScrollSync targetRef={scrollContainerRef} />

      {/* Main Table Scroll Container */}
      <div
        ref={scrollContainerRef}
        data-testid="sales-order-grid-container"
        className="relative max-h-[calc(100vh-220px)] overflow-x-auto overflow-y-auto rounded-lg border border-slate-300 bg-white shadow-sm"
        style={{ maxHeight: 'calc(100vh - 220px)', minHeight: '360px' }}
      >
        <table className={`w-full border-collapse text-left text-sm ${isSticky ? 'has-sticky' : ''}`}>
          <thead className="sticky top-0 z-20 bg-slate-100 text-xs font-bold uppercase tracking-wider text-slate-700 select-none shadow-sm">
            <tr>
              {/* Sticky Columns 1..5 */}
              <th
                className={`border-b-2 border-r border-slate-300 px-2 py-3 text-center ${
                  isSticky ? 'sticky left-0 z-40 bg-slate-100' : ''
                }`}
                style={{ width: 42, minWidth: 42 }}
              >
                #
              </th>

              <th
                className={`border-b-2 border-r border-slate-300 px-2.5 py-3 ${
                  isSticky ? 'sticky left-[42px] z-40 bg-slate-100' : ''
                }`}
                style={{ width: 170, minWidth: 170 }}
              >
                Khách hàng <span className="text-rose-600">*</span>
              </th>

              <th
                className={`border-b-2 border-r border-slate-300 px-2.5 py-3 ${
                  isSticky ? 'sticky left-[212px] z-40 bg-slate-100' : ''
                }`}
                style={{ width: 150, minWidth: 150 }}
              >
                Người mua
              </th>

              <th
                className={`border-b-2 border-r border-slate-300 px-1 py-3 text-center ${
                  isSticky ? 'sticky left-[362px] z-40 bg-slate-100' : ''
                }`}
                style={{ width: 70, minWidth: 70 }}
              >
                Ảnh
              </th>

              <th
                className={`border-b-2 border-r border-slate-300 px-2.5 py-3 ${
                  isSticky
                    ? 'sticky left-[432px] z-40 bg-slate-100 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.15)] border-r-2 border-r-slate-400'
                    : ''
                }`}
                style={{ width: 180, minWidth: 180 }}
              >
                Mã hàng <span className="text-rose-600">*</span>
              </th>

              {/* Scrollable Columns */}
              <th className="border-b-2 border-r border-slate-300 px-2 py-3" style={{ width: 140, minWidth: 140 }}>
                Màu
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3" style={{ width: 200, minWidth: 200 }}>
                Tên sản phẩm
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2 py-3 text-right" style={{ width: 75, minWidth: 75 }}>
                W
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2 py-3 text-right" style={{ width: 75, minWidth: 75 }}>
                D
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2 py-3 text-right" style={{ width: 75, minWidth: 75 }}>
                H
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3" style={{ width: 130, minWidth: 130 }}>
                Ráp / Tháo <span className="text-rose-600">*</span>
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3" style={{ width: 115, minWidth: 115 }}>
                ISTA <span className="text-rose-600">*</span>
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2 py-3 text-right" style={{ width: 110, minWidth: 110 }}>
                Số lượng <span className="text-rose-600">*</span>
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3" style={{ width: 130, minWidth: 130 }}>
                PO khách
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2 py-3 text-right" style={{ width: 85, minWidth: 85 }}>
                Cái/thùng
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3" style={{ width: 160, minWidth: 160 }}>
                Ghi chú
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3 leading-tight" style={{ width: 150, minWidth: 150 }}>
                Ngày khách YC<br />hoàn thành
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2.5 py-3" style={{ width: 135, minWidth: 135 }}>
                Ngày cập nhật
              </th>

              <th className="border-b-2 border-r border-slate-300 px-2 py-3 text-center" style={{ width: 95, minWidth: 95 }}>
                Trạng thái
              </th>

              <th className="border-b-2 border-slate-300 px-1 py-3 text-center" style={{ width: 48, minWidth: 48 }}>
                ⋮
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {lines.map((line, idx) => {
              const isDraft = line.line_status === 'draft'
              const isSx = line.line_status === 'sx'
              const isCancelled = line.line_status === 'cancelled'
              const isLocked = !canWrite || isCancelled

              // Row styling
              let rowClass = 'transition-colors '
              if (isDraft) {
                rowClass += 'row-status-draft bg-slate-50/70 text-slate-600 '
              } else if (isSx) {
                rowClass += 'row-status-sx border-l-[5px] border-l-emerald-600 bg-white '
              } else if (isCancelled) {
                rowClass += 'row-status-cancelled bg-rose-50/60 text-rose-900 line-through opacity-80 '
              } else {
                rowClass += 'bg-white hover:bg-slate-50/60 '
              }

              // Missing fields check
              const isLineErrorHighlight = errorHighlightFields?.lineId === line.id
              const highlightedFields = isLineErrorHighlight ? errorHighlightFields?.fields || [] : []

              const isCustMissing = highlightedFields.includes('customer_id') || (!line.customer_id && !line.customer_code)
              const isVarMissing = highlightedFields.includes('variant_code') || (!line.variant_id && !line.variant_code)
              const isAssemblyMissing = highlightedFields.includes('assembly_type') || !line.assembly_type
              const isIstaMissing = highlightedFields.includes('ista_level') || !line.ista_level
              const isQtyMissing = highlightedFields.includes('quantity') || Number(line.quantity) < 1

              // Current line unit
              const lineUnit = line.dim_unit || 'inch'

              // Available buyers for this row
              const rowBuyers = line.customer_id ? customerBuyersMap[line.customer_id] || [] : []

              // Sticky cell background
              const stickyBg = isCancelled
                ? 'bg-rose-50'
                : isDraft
                  ? 'bg-slate-50'
                  : 'bg-white'

              return (
                <tr key={line.tempKey || line.id || idx} className={rowClass} style={{ height: 60 }}>
                  {/* 1. # */}
                  <td
                    className={`border-r border-slate-200 text-center font-mono text-xs text-slate-400 ${
                      isSticky ? `sticky left-0 z-10 ${stickyBg}` : ''
                    }`}
                  >
                    {idx + 1}
                  </td>

                  {/* 2. Khách hàng */}
                  <td
                    className={`border-r border-slate-200 p-1 ${
                      isSticky ? `sticky left-[42px] z-10 ${stickyBg}` : ''
                    } ${isLocked ? 'cell-permission-locked' : ''}`}
                  >
                    {isLocked ? (
                      <span className="block truncate font-mono font-bold text-slate-700 px-2">
                        {line.customer_code || '—'}
                      </span>
                    ) : (
                      <div
                        className={`rounded ${
                          line.isCopiedCustomer ? 'bg-emerald-50 ring-1 ring-emerald-300' : ''
                        } ${isCustMissing && isLineErrorHighlight ? 'ring-2 ring-rose-500 bg-rose-50' : ''}`}
                        title={line.isCopiedCustomer ? 'Được sao chép từ dòng trên' : undefined}
                      >
                        <Typeahead<Customer>
                          items={activeCustomers}
                          value={activeCustomers.find((c) => c.id === line.customer_id) || null}
                          displayValue={(c) => (c ? `${c.code} - ${c.name}` : '')}
                          placeholder="Mã/tên khách..."
                          onChange={(c) => handleCustomerChange(idx, c)}
                          inputClassName={`h-9 font-mono font-bold text-xs ${
                            isCustMissing ? 'border-amber-300 bg-amber-50 text-amber-900' : ''
                          }`}
                        />
                      </div>
                    )}
                  </td>

                  {/* 3. Người mua */}
                  <td
                    className={`border-r border-slate-200 p-1 ${
                      isSticky ? `sticky left-[212px] z-10 ${stickyBg}` : ''
                    } ${isLocked ? 'cell-permission-locked' : ''}`}
                  >
                    {isLocked ? (
                      <span className="block truncate text-slate-700 px-2">{line.buyer_name || '—'}</span>
                    ) : !line.customer_id ? (
                      <span className="block px-2 text-xs italic text-slate-400 select-none">
                        Chọn khách trước
                      </span>
                    ) : (
                      <div
                        className={`rounded ${
                          line.isCopiedBuyer ? 'bg-emerald-50 ring-1 ring-emerald-300' : ''
                        }`}
                      >
                        <Typeahead<CustomerBuyer>
                          items={rowBuyers}
                          value={rowBuyers.find((b) => b.id === line.buyer_id) || null}
                          displayValue={(b) => b?.name || ''}
                          placeholder="Chọn người mua..."
                          onChange={(b) => handleBuyerChange(idx, b)}
                          inputClassName="h-9 text-xs"
                        />
                      </div>
                    )}
                  </td>

                  {/* 4. Ảnh 56px */}
                  <td
                    className={`border-r border-slate-200 p-1 text-center ${
                      isSticky ? `sticky left-[362px] z-10 ${stickyBg}` : ''
                    }`}
                  >
                    <div
                      className="mx-auto flex h-12 w-12 cursor-pointer items-center justify-center rounded border border-slate-200 bg-slate-50 transition-transform hover:scale-105 hover:border-emerald-600"
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect()
                        setHoverCard({
                          visible: true,
                          line,
                          x: rect.right + 12,
                          y: rect.top - 20,
                        })
                      }}
                      onMouseLeave={() => setHoverCard({ visible: false, line: null, x: 0, y: 0 })}
                    >
                      {line.product_id ? (
                        <img
                          src={`/api/products/${encodeURIComponent(line.product_id)}/image`}
                          alt={line.variant_code || 'Product'}
                          className="h-full w-full rounded object-cover"
                          onError={(e) => {
                            // Fallback svg if image 404
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : (
                        <span className="text-xs text-slate-300">📦</span>
                      )}
                    </div>
                  </td>

                  {/* 5. Mã hàng */}
                  <td
                    className={`border-r border-slate-200 p-1 ${
                      isSticky
                        ? `sticky left-[432px] z-10 ${stickyBg} shadow-[4px_0_6px_-2px_rgba(0,0,0,0.15)] border-r-2 border-r-slate-400`
                        : ''
                    } ${isLocked ? 'cell-permission-locked' : ''}`}
                  >
                    {isLocked ? (
                      <span className="block truncate font-mono font-bold text-emerald-800 px-2">
                        {line.variant_code || '—'}
                      </span>
                    ) : (
                      <div
                        className={isVarMissing && isLineErrorHighlight ? 'ring-2 ring-rose-500 rounded bg-rose-50' : ''}
                      >
                        <Typeahead<VariantSearchResult>
                          items={variantList}
                          value={variantList.find((v) => v.id === line.variant_id) || null}
                          displayValue={(v) => v?.variant_code || ''}
                          placeholder="Gõ mã hàng..."
                          onChange={(v) => handleVariantChange(idx, v)}
                          inputClassName={`h-9 font-mono font-bold text-xs text-emerald-800 ${
                            isVarMissing ? 'border-amber-300 bg-amber-50 text-amber-900' : ''
                          }`}
                        />
                      </div>
                    )}
                  </td>

                  {/* 6. Màu (khối màu thật 28px) */}
                  <td className="border-r border-slate-200 px-2 py-1">
                    {line.color_name ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="h-7 w-7 flex-shrink-0 rounded-md border border-slate-400 shadow-inner"
                          style={{ backgroundColor: line.color_swatch_hex || '#cbd5e1' }}
                          title={line.color_swatch_hex || undefined}
                        />
                        <span className="truncate text-xs font-semibold text-slate-700">{line.color_name}</span>
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>

                  {/* 7. Tên sản phẩm */}
                  <td className="border-r border-slate-200 px-2.5 py-1">
                    <span className="block truncate text-xs font-medium text-slate-800" title={line.product_name || ''}>
                      {line.product_name || <em className="text-slate-300">Chưa chọn</em>}
                    </span>
                  </td>

                  {/* 8, 9, 10: W, D, H (số thuần trong đơn vị lineUnit) */}
                  <td className="border-r border-slate-200 px-2 py-1 text-right font-mono text-xs">
                    {formatLineDim(line.w, lineUnit)}
                  </td>
                  <td className="border-r border-slate-200 px-2 py-1 text-right font-mono text-xs">
                    {formatLineDim(line.d, lineUnit)}
                  </td>
                  <td className="border-r border-slate-200 px-2 py-1 text-right font-mono text-xs">
                    {formatLineDim(line.h, lineUnit)}
                  </td>

                  {/* 11. Ráp / Tháo */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block px-2 text-xs font-medium text-slate-700">
                        {line.assembly_type === 'assembled'
                          ? 'Ráp chết'
                          : line.assembly_type === 'kd'
                            ? 'Tháo rời'
                            : '—'}
                      </span>
                    ) : (
                      <select
                        className={`h-9 w-full rounded border px-2 text-xs font-medium outline-none focus:border-emerald-600 ${
                          !line.assembly_type
                            ? 'cell-warning-bg border-amber-300 bg-amber-50 text-amber-900 font-semibold'
                            : 'border-slate-300 bg-white text-slate-800'
                        } ${isAssemblyMissing && isLineErrorHighlight ? 'ring-2 ring-rose-500' : ''}`}
                        value={line.assembly_type}
                        onChange={(e) =>
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            assembly_type: e.target.value as any,
                          }))
                        }
                      >
                        <option value="">Chọn…</option>
                        <option value="assembled">Ráp chết</option>
                        <option value="kd">Tháo rời</option>
                      </select>
                    )}
                  </td>

                  {/* 12. ISTA */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block px-2 text-xs font-medium text-slate-700">
                        {line.ista_level ? `ISTA ${line.ista_level}` : '—'}
                      </span>
                    ) : (
                      <select
                        className={`h-9 w-full rounded border px-2 text-xs font-medium outline-none focus:border-emerald-600 ${
                          !line.ista_level
                            ? 'cell-warning-bg border-amber-300 bg-amber-50 text-amber-900 font-semibold'
                            : 'border-slate-300 bg-white text-slate-800'
                        } ${isIstaMissing && isLineErrorHighlight ? 'ring-2 ring-rose-500' : ''}`}
                        value={line.ista_level}
                        onChange={(e) =>
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            ista_level: e.target.value as any,
                          }))
                        }
                      >
                        <option value="">Chọn…</option>
                        <option value="1A">ISTA 1A</option>
                        <option value="3A">ISTA 3A</option>
                      </select>
                    )}
                  </td>

                  {/* 13. Số lượng */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block px-2 text-right font-mono font-bold text-xs text-slate-800">
                        {(line.quantity || 0).toLocaleString('vi-VN')}
                      </span>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        max="999999"
                        className={`h-9 w-full rounded border px-2 text-right font-mono font-bold text-xs outline-none focus:border-emerald-600 ${
                          isQtyMissing && isLineErrorHighlight
                            ? 'ring-2 ring-rose-500 bg-rose-50 text-rose-900'
                            : 'border-slate-300 bg-white text-slate-800'
                        }`}
                        value={line.quantity === 0 ? '' : line.quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10)
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            quantity: isNaN(val) ? 0 : Math.min(Math.max(val, 0), 999999),
                          }))
                        }}
                      />
                    )}
                  </td>

                  {/* 14. PO khách */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block truncate px-2 font-mono text-xs text-slate-700">
                        {line.customer_po || '—'}
                      </span>
                    ) : (
                      <input
                        type="text"
                        className="h-9 w-full rounded border border-slate-300 px-2 font-mono text-xs outline-none focus:border-emerald-600 bg-white text-slate-800"
                        value={line.customer_po || ''}
                        placeholder="PO..."
                        onChange={(e) =>
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            customer_po: e.target.value,
                          }))
                        }
                      />
                    )}
                  </td>

                  {/* 15. Cái/thùng */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block px-2 text-right font-mono text-xs text-slate-700">
                        {line.pcs_per_carton != null ? line.pcs_per_carton : '—'}
                      </span>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        className="h-9 w-full rounded border border-slate-300 px-2 text-right font-mono text-xs outline-none focus:border-emerald-600 bg-white text-slate-800"
                        value={line.pcs_per_carton != null ? line.pcs_per_carton : ''}
                        placeholder=""
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10)
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            pcs_per_carton: isNaN(val) ? null : val,
                          }))
                        }}
                      />
                    )}
                  </td>

                  {/* 16. Ghi chú */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block truncate px-2 text-xs text-slate-600">
                        {line.packing_note || '—'}
                      </span>
                    ) : (
                      <input
                        type="text"
                        className="h-9 w-full rounded border border-slate-300 px-2 text-xs outline-none focus:border-emerald-600 bg-white text-slate-800"
                        value={line.packing_note || ''}
                        placeholder="Ghi chú đóng gói..."
                        onChange={(e) =>
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            packing_note: e.target.value,
                          }))
                        }
                      />
                    )}
                  </td>

                  {/* 17. Ngày khách YC hoàn thành (DateInput dd/mm) */}
                  <td className={`border-r border-slate-200 p-1 ${isLocked ? 'cell-permission-locked' : ''}`}>
                    {isLocked ? (
                      <span className="block px-2 font-mono text-xs text-slate-700">
                        {line.requested_ship_date ? formatDate(line.requested_ship_date) : '—'}
                      </span>
                    ) : (
                      <DateInput
                        value={line.requested_ship_date || ''}
                        onChange={(d) =>
                          updateSingleLine(idx, (l) => ({
                            ...l,
                            requested_ship_date: d,
                          }))
                        }
                        className="h-9 w-full text-xs font-mono"
                        placeholder="dd/mm"
                      />
                    )}
                  </td>

                  {/* 18. Ngày cập nhật */}
                  <td className="border-r border-slate-200 px-2 py-1 text-xs font-mono text-slate-500">
                    {line.updated_at ? formatDateTime(line.updated_at) : '—'}
                  </td>

                  {/* 19. Trạng thái */}
                  <td className="border-r border-slate-200 px-2 py-1 text-center">
                    {isDraft && (
                      <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                        Nháp
                      </span>
                    )}
                    {isSx && (
                      <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">
                        ✓ SX
                      </span>
                    )}
                    {isCancelled && (
                      <span
                        className="inline-flex items-center rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-800"
                        title={line.status_reason || 'Đã hủy'}
                      >
                        ✕ Hủy
                      </span>
                    )}
                  </td>

                  {/* 20. Menu ⋮ */}
                  <td className="px-1 py-1 text-center">
                    <button
                      type="button"
                      className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800 active:bg-slate-300"
                      onClick={(e) => {
                        e.stopPropagation()
                        const rect = e.currentTarget.getBoundingClientRect()
                        setMenuState({ line, anchorRect: rect })
                      }}
                      title="Thao tác dòng"
                    >
                      ⋮
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Floating 3-dots Context Menu */}
      <SalesOrderRowMenu
        line={menuState.line || ({} as any)}
        orderId={orderId}
        canWrite={canWrite}
        canDemandWrite={canDemandWrite}
        isOpen={Boolean(menuState.line && menuState.anchorRect)}
        anchorRect={menuState.anchorRect}
        onClose={() => setMenuState({ line: null, anchorRect: null })}
        onFocusFirstEditable={() => {
          if (!menuState.line) return
          const idx = lines.findIndex((l) => l.tempKey === menuState.line?.tempKey)
          if (idx !== -1) {
            cellRefs.current[`cell-${idx}-customer`]?.focus()
          }
        }}
        onDuplicate={() => {
          if (!menuState.line) return
          const idx = lines.findIndex((l) => l.tempKey === menuState.line?.tempKey)
          if (idx !== -1) handleDuplicateLine(idx)
        }}
        onViewHistory={() => {
          setHistoryModalLine(menuState.line)
        }}
        onSelectDraft={() => {
          if (!menuState.line) return
          if (menuState.line.line_status === 'cancelled') {
            // Restore requires reason
            setStatusModalState({
              isOpen: true,
              line: menuState.line,
              targetStatus: 'draft',
            })
          } else {
            setStatusModalState({
              isOpen: true,
              line: menuState.line,
              targetStatus: 'draft',
            })
          }
        }}
        onSelectSx={() => {
          if (!menuState.line) return
          // Validate required fields before transitioning to SX
          const validation = validateGridLine(menuState.line)
          if (!validation.valid) {
            const fieldLabels: Record<string, string> = {
              customer_id: 'Khách hàng',
              variant_code: 'Mã hàng',
              assembly_type: 'Ráp/Tháo',
              ista_level: 'ISTA',
              quantity: 'Số lượng',
            }
            const missingNames = validation.missingFields.map((f) => fieldLabels[f] || f)
            alert(`⚠️ Không thể chuyển sang SX! Dòng còn thiếu:\n• ${missingNames.join('\n• ')}`)
            return
          }

          setStatusModalState({
            isOpen: true,
            line: menuState.line,
            targetStatus: 'sx',
          })
        }}
        onSelectCancel={() => {
          if (!menuState.line) return
          setStatusModalState({
            isOpen: true,
            line: menuState.line,
            targetStatus: 'cancelled',
          })
        }}
        onDelete={() => {
          if (!menuState.line) return
          const idx = lines.findIndex((l) => l.tempKey === menuState.line?.tempKey)
          if (idx !== -1) handleDeleteLine(idx)
        }}
        onToggleUnit={() => {
          if (!menuState.line) return
          const idx = lines.findIndex((l) => l.tempKey === menuState.line?.tempKey)
          if (idx !== -1) handleToggleUnit(idx)
        }}
        onPushToDemand={() => {
          setDemandModalLine(menuState.line)
        }}
      />

      {/* Hover Image Preview Modal */}
      {hoverCard.visible && hoverCard.line && (
        <div
          style={{
            position: 'fixed',
            left: `${hoverCard.x}px`,
            top: `${hoverCard.y}px`,
            zIndex: 9999999,
          }}
          className="pointer-events-none w-80 rounded-lg border border-slate-300 bg-white p-3 shadow-2xl animate-in fade-in zoom-in-95 duration-100"
        >
          <div className="flex h-48 w-full items-center justify-center rounded border border-slate-200 bg-slate-50 overflow-hidden">
            {hoverCard.line.product_id ? (
              <img
                src={`/api/products/${encodeURIComponent(hoverCard.line.product_id)}/image`}
                alt={hoverCard.line.variant_code || 'Preview'}
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-3xl text-slate-300">📦</span>
            )}
          </div>
          <div className="mt-2 text-xs space-y-1">
            <div className="flex items-center justify-between font-mono font-bold text-slate-800">
              <span>{hoverCard.line.variant_code || '—'}</span>
              <span className="text-slate-500">{hoverCard.line.color_name}</span>
            </div>
            <p className="font-semibold text-slate-700">{hoverCard.line.product_name}</p>
            <p className="font-mono text-slate-500">
              W {formatLineDim(hoverCard.line.w, hoverCard.line.dim_unit || 'inch')} x D{' '}
              {formatLineDim(hoverCard.line.d, hoverCard.line.dim_unit || 'inch')} x H{' '}
              {formatLineDim(hoverCard.line.h, hoverCard.line.dim_unit || 'inch')} (
              {hoverCard.line.dim_unit || 'inch'})
            </p>
          </div>
        </div>
      )}

      {/* Status Reason Modal */}
      <StatusReasonModal
        isOpen={statusModalState.isOpen}
        line={statusModalState.line}
        targetStatus={statusModalState.targetStatus}
        onClose={() =>
          setStatusModalState({ isOpen: false, line: null, targetStatus: 'draft' })
        }
        onConfirm={handleConfirmStatusChange}
      />

      {/* Line History Modal */}
      <SalesOrderLineHistoryModal
        isOpen={Boolean(historyModalLine)}
        line={historyModalLine}
        orderId={orderId}
        onClose={() => setHistoryModalLine(null)}
      />

      {/* Push to Demand Modal */}
      <PushToDemandModal
        isOpen={Boolean(demandModalLine)}
        line={demandModalLine}
        orderCode={orderCode}
        onClose={() => setDemandModalLine(null)}
        onSuccess={(batchId) => {
          showToast(`✓ Đã đưa dòng vào đợt nhu cầu thành công!`)
          // Update demand status in line
          if (demandModalLine) {
            const idx = lines.findIndex((l) => l.tempKey === demandModalLine.tempKey)
            if (idx !== -1) {
              updateSingleLine(idx, (l) => ({
                ...l,
                demand_status: 'PUSHED',
                demand_batch_ids: [...(l.demand_batch_ids || []), batchId],
              }))
            }
          }
        }}
      />
    </div>
  )
}
